
import { Col } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import Sidebar from 'components/layout/Sidebar';
import reportActions from 'features/report/services/actions';
import reportSelectors from 'features/report/services/selectors';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import PrintHeader from '../components/PrintHeader';
import { ReportType } from '../constants';
import { renderTime, formatShopInfo } from '../utils';
import shopSelectors from 'services/shop/selectors';

interface IProductReportPrintPage { }
const ProductReportPrintPage: React.FC<IProductReportPrintPage> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // const dataProductReport = reportSelectors.getDataProductReport();
  const dataProductReport = reportSelectors.getDataProductReportV2();
  const dataAppointment = reportSelectors.getParams()?.[ReportType.PRODUCTS];
  const shopInfo = shopSelectors.data.shopInfo();
  const shopInfoFormatted = formatShopInfo(shopInfo);
  const allSetting = shopSelectors.data.allSetting();
  const v2 = allSetting?.versionUI === 'V2';

  useEffect(() => {
    if (!dataAppointment) return;
    dispatch(reportActions.getProductReport.fetch({
      startDate: dataAppointment.startTime,
      endDate: dataAppointment.endTime
    }));
  }, [dataAppointment]);
  const onBack = () => {
    navigate(-1);
  };
  const divRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({
    content: () => divRef.current,
    copyStyles: true,
  });
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <ContainerStyled>
        <PrintHeader v2={v2} onBack={onBack} onPrint={onPrint} />
        <div ref={divRef}>
          <Box className="center" overflow="auto">
            <TicketBox>
              <LogoBox>
                <ShopName>{shopInfoFormatted.name}</ShopName>
                <ShopAddress>{shopInfoFormatted.streetAddress}</ShopAddress>
                <ShopAddress>{shopInfoFormatted.cityStateZip}</ShopAddress>
                <ShopPhone>{shopInfoFormatted.phone}</ShopPhone>
              </LogoBox>
              <Box className="center">
                <Text variant="CONTENT_2" color="text_3" printMode>
                  {renderTime(dataAppointment)}
                </Text>
              </Box>
              <Box>
                <Category>
                  <Box display="flex" flexDirection="column" >
                    <Box className="space-between" bb="print_line" style={{ borderBottomStyle: 'dashed' }} >
                      <Text style={{ flex: 1 }} variant="CAPTION_2" printMode>
                        PRODUCTS
                      </Text>
                      <Text style={{ flex: 0.5, textAlign: 'right' }} variant="CAPTION_1" color="text_2" printMode>
                        DISC
                      </Text>
                      <Text style={{ flex: 1, textAlign: 'right' }} variant="CAPTION_1" color="text_2" printMode>
                        SALES
                      </Text>
                    </Box>
                    {dataProductReport?.map((o, index) => (
                      <Box key={o.id} display='flex' flexDirection='column' style={{ marginBottom: 4 }}>
                        <Box className="space-between" bb="print_line" highlight>
                          <Text printMode variant="CAPTION_2" color="text_3" className='title-highlight fs-lg category-name'>
                            {o.categoryName}
                          </Text>
                          <Text printMode variant="CAPTION_2" color="text_3" className='text-highlight fs-lg'>
                            {formatCurrency(o.sale)}
                          </Text>
                        </Box>
                        {o.products.map(item => (
                          <Box key={item.productId} className="space-between" bb="print_line" style={{ borderBottomStyle: 'dotted' }}
                            bStyle="dotted">
                            <Text style={{ flex: 1 }} variant="CAPTION_2" printMode>
                              {item.productName}
                            </Text>
                            <Text style={{ flex: 0.5, textAlign: 'right' }} variant="CAPTION_1" color="text_2" printMode>
                              {formatCurrency(item.discount)}
                            </Text>
                            <Text style={{ flex: 1, textAlign: 'right' }} variant="CAPTION_1" color="text_2" printMode>
                              {formatCurrency(item.totalSale)}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                  <Box style={{ marginBottom: 4 }} className="space-between" bb="print_line" highlight>
                    <Text printMode variant="CAPTION_2" color="text_3" className='title-highlight fs-lg'>
                      tOTAL DISC
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3" className='text-highlight fs-lg'>
                      {formatCurrency(dataProductReport?.reduce((total, cur) => total + cur.discount, 0) ?? 0)}
                    </Text>
                  </Box>
                  <Box className="space-between" bb="print_line" highlight>
                    <Text printMode variant="CAPTION_2" color="text_3" className='title-highlight fs-lg'>
                      tOTAL SALE
                    </Text>
                    <Text printMode variant="CAPTION_2" color="text_3" className='text-highlight fs-lg'>
                      {formatCurrency(dataProductReport?.reduce((total, cur) => total + cur.sale, 0) ?? 0)}
                    </Text>
                  </Box>
                </Category>
              </Box>
            </TicketBox>
          </Box>
        </div>
      </ContainerStyled>
    </div>
  );
};

export default ProductReportPrintPage;

const TicketBox = styled.div`
  border-radius: 20px;
  background: #fff;
  width: 19.5rem;
  height: fit-content;
  padding: 1.5rem 0 2rem;
`;

const ContainerStyled = styled.div`
  background-color: #e5e5e5;
  height: 100vh;
  position: relative;
  flex-grow: 1;
  width: 100%;
  overflow: auto;
`;
const LogoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1px;
  text-align: center;
`;

const ShopName = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
  color: black;
`;

const ShopAddress = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  color: black;
`;

const ShopPhone = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  color: black;
`;

const Category = styled(Box)`
  padding: 5px 4px; 
  .category-name {
    flex: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    color: #1D2129;
    text-overflow: ellipsis;
    text-align: left;
  }
`;

